import { Delete, SettingsBackupRestore, Edit } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { UseTableCellProps } from 'react-table';
import { CustomerManagementGridItem, CustomerNotificationTemplate } from 'shared/types/customerManagement';
import { CustomerDeleteDialog } from './customer-delete-dialog';
import { useState } from 'react';
import { useCreateCustomer, useCreateCustomerToDelete, useSendCustomerDeleteNotification } from 'shared/mutations';
import { useService } from 'shared/providers';
import { CustomerLookup, CustomerToDelete } from 'shared/types';
import { useAuthState } from 'shared/stores';
import { formatAsCosmosDateString } from 'shared/utility/formats';
import { useToggle, useUserPermissions } from 'shared/hooks';
import { boxStyles } from 'customer-management/styles';
import { EditCustomerDialog } from 'customer-management/edit-customer-dialog';

export function ActionColumn(props: UseTableCellProps<CustomerManagementGridItem>) {
  const [customerDeleteDialogOpen, setCustomerDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [typeRequest, setTypeRequest] = useState<CustomerNotificationTemplate>('delete');
  const [editCustomerDialogOpen, editCustomerToggleDialog] = useToggle();
  const [customer, setCustomer] = useState<CustomerLookup>({
    uniqueid: '',
    abgmastercustomer: '',
    brand: '',
    channel: '',
    l12mproductgrossrevenue: '',
    customertier: '',
    numberofskus: '',
    totalgtn: '',
    numberofcontracts: '',
    pricingtype: '',
    pricinglist: '',
    isdeleted: '',
    ismarkedfordeletion: '',
  });
  const createCustomerMutation = useCreateCustomer('update');
  const createCustomerToDeleteMutation = useCreateCustomerToDelete();
  const notificationMutation = useSendCustomerDeleteNotification();

  const userPermissions = useUserPermissions();
  const hasCustomerDeletionApprovePermission = userPermissions.includes('abg-retail.customer.approve');
  const hasCustomerDeletionRequestPermission = userPermissions.includes('abg-retail.customer.request');

  const { customerService, appConfig } = useService();
  const authState = useAuthState();
  const {
    row: {
      original: { abgBrand, channel, customerTier, masterCustomer, isMarkedForDeletion, isDeleted },
    },
  } = props;

  const deleteCustomer = async () => {
    setIsLoading(true);
    const customers = await customerService.getCustomersLookupWithFilters({
      channel: channel,
      customerTier: customerTier,
      abgBrand,
      masterCustomer,
    });
    if (customers && customers?.length > 0) {
      const customerUpdate = customers[0];
      if (typeRequest === 'restore' || typeRequest === 'reject') {
        customerUpdate.isdeleted = 'FALSE';
        customerUpdate.ismarkedfordeletion = 'FALSE';
      } else if (typeRequest === 'approve') {
        customerUpdate.isdeleted = 'TRUE';
        customerUpdate.ismarkedfordeletion = 'FALSE';
      } else {
        customerUpdate.ismarkedfordeletion = 'TRUE';
      }
      await createCustomerMutation.mutateAsync(customerUpdate);
      const customerToDelete: CustomerToDelete = {
        uniqueid: customerUpdate.uniqueid,
        useremail: authState.get()?.user?.email || '',
        ismarkedfordeletion: customerUpdate.ismarkedfordeletion,
        requestdate: formatAsCosmosDateString(new Date()),
      };
      if (typeRequest === 'delete') {
        await createCustomerToDeleteMutation.mutateAsync(customerToDelete);
      }
      await notificationMutation.mutateAsync({
        customer: customerUpdate,
        template: typeRequest,
        userName:
          authState.get()?.user?.displayName ||
          `${authState.get()?.user?.firstName} ${authState.get()?.user?.lastName}`,
        environment: appConfig.environment,
      });
    }
    setCustomerDeleteDialogOpen(false);
    setIsLoading(false);
  };

  const openDeleteCustomerDialog = (type: CustomerNotificationTemplate) => {
    setTypeRequest(type);
    setCustomerDeleteDialogOpen(true);
  };

  const editCustomer = async () => {
    const customers = await customerService.getCustomersLookupWithFilters({
      channel: channel,
      customerTier: customerTier,
      abgBrand,
      masterCustomer,
    });
    const customer = customers[0];
    setCustomer(customer);
    setIsLoading(true);
    editCustomerToggleDialog();
  };

  return (
    <Box sx={boxStyles}>
      {hasCustomerDeletionRequestPermission && (
        <Box>
          <IconButton
            disabled={isMarkedForDeletion && !isDeleted}
            onClick={() => openDeleteCustomerDialog(isDeleted ? 'restore' : 'delete')}
            aria-label={`${isDeleted ? 'restore' : 'delete'} customer`}
            color={`${isDeleted ? 'success' : 'error'}`}
          >
            {isDeleted ? <SettingsBackupRestore /> : <Delete />}
          </IconButton>
        </Box>
      )}
      {hasCustomerDeletionApprovePermission && (
        <Box display={'flex'}>
          <Box>
            <IconButton
              disabled={!isMarkedForDeletion && !isDeleted}
              onClick={() => openDeleteCustomerDialog(isDeleted ? 'restore' : 'reject')}
              aria-label='restore customer'
              color='success'
            >
              <SettingsBackupRestore />
            </IconButton>
          </Box>
          <Box>
            <IconButton
              disabled={isDeleted}
              onClick={() => openDeleteCustomerDialog('approve')}
              aria-label='delete customer'
              color='error'
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
      )}
      <Box>
        <IconButton onClick={() => editCustomer()} aria-label='edit customer'>
          <Edit />
        </IconButton>
      </Box>
      <CustomerDeleteDialog
        customerDeleteDialogOpen={customerDeleteDialogOpen}
        isLoading={isLoading}
        onClose={() => setCustomerDeleteDialogOpen(false)}
        onDelete={deleteCustomer}
        typeRequest={typeRequest}
      />
      <EditCustomerDialog
        isOpen={editCustomerDialogOpen}
        toggleDialog={editCustomerToggleDialog}
        currentCustomer={customer}
      />
    </Box>
  );
}
